import React, {
  useRef,
  useEffect,
  useState,
  useCallback,
  useMemo,
} from "react";
import { Event, Agent, AGENT_COLORS, EVENT_COLORS } from "@/shared/types";

interface WaterfallVisualizationProps {
  events: Event[];
  agents: Agent[];
  isPlaying: boolean;
  currentTime: number;
  speed: number;
  zoom: number;
  onTimeChange: (time: number) => void;
  onEventClick?: (event: Event) => void;
  className?: string;
}

interface Point {
  x: number;
  y: number;
}

export const WaterfallVisualization: React.FC<WaterfallVisualizationProps> = ({
  events,
  agents,
  isPlaying,
  currentTime,

  zoom,
  onTimeChange,
  onEventClick,
  className = "",
}) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const animationRef = useRef<number>();

  // State for interaction
  const [canvasSize, setCanvasSize] = useState({ width: 0, height: 0 });
  const [panOffset, setPanOffset] = useState<Point>({ x: 0, y: 0 });
  const [isPanning, setIsPanning] = useState(false);
  const [panStart, setPanStart] = useState<Point>({ x: 0, y: 0 });
  const [hoveredEvent, setHoveredEvent] = useState<Event | null>(null);
  const [mousePosition, setMousePosition] = useState<Point>({ x: 0, y: 0 });

  // Constants
  const LANE_HEIGHT = 60;
  const AGENT_WIDTH = 150;
  const TIMELINE_HEIGHT = 40;
  const EVENT_HEIGHT = 24;
  const PIXELS_PER_SECOND = 80 * (zoom / 100);

  // Calculate max time for timeline
  const maxTime = useMemo(() => {
    if (events.length === 0) return 10;
    return Math.max(...events.map((e) => e.timestamp)) + 2;
  }, [events]);

  // Visible events based on current time
  const visibleEvents = useMemo(() => {
    return events.filter((event) => event.timestamp <= currentTime);
  }, [events, currentTime]);

  // Agent position mapping
  const agentPositions = useMemo(() => {
    const positions: Record<string, number> = {};
    agents.forEach((agent, index) => {
      positions[agent.id] = index;
    });
    return positions;
  }, [agents]);

  // Resize handler
  const handleResize = useCallback(() => {
    if (!containerRef.current) return;

    const rect = containerRef.current.getBoundingClientRect();
    const newSize = {
      width: rect.width,
      height: Math.max(agents.length * LANE_HEIGHT + TIMELINE_HEIGHT, 400),
    };

    setCanvasSize(newSize);

    if (canvasRef.current) {
      const dpr = window.devicePixelRatio || 1;
      canvasRef.current.width = newSize.width * dpr;
      canvasRef.current.height = newSize.height * dpr;
      canvasRef.current.style.width = `${newSize.width}px`;
      canvasRef.current.style.height = `${newSize.height}px`;

      const ctx = canvasRef.current.getContext("2d");
      if (ctx) {
        ctx.scale(dpr, dpr);
      }
    }
  }, [agents.length]);

  // Mouse event handlers
  const handleMouseDown = useCallback(
    (e: React.MouseEvent<HTMLCanvasElement>) => {
      if (e.button !== 0) return; // Only left click

      setIsPanning(true);
      setPanStart({ x: e.clientX - panOffset.x, y: e.clientY - panOffset.y });
      e.preventDefault();
    },
    [panOffset],
  );

  const handleMouseMove = useCallback(
    (e: React.MouseEvent<HTMLCanvasElement>) => {
      const rect = canvasRef.current?.getBoundingClientRect();
      if (!rect) return;

      const mouseX = e.clientX - rect.left;
      const mouseY = e.clientY - rect.top;
      setMousePosition({ x: mouseX, y: mouseY });

      if (isPanning) {
        setPanOffset({
          x: e.clientX - panStart.x,
          y: e.clientY - panStart.y,
        });
        return;
      }

      // Check for event hover
      const adjustedX = mouseX - panOffset.x - AGENT_WIDTH;
      const adjustedY = mouseY - panOffset.y - TIMELINE_HEIGHT;

      let foundEvent: Event | null = null;

      for (const event of visibleEvents) {
        const agentIndex = agentPositions[event.agentId];
        if (agentIndex === undefined) continue;

        const eventX = event.timestamp * PIXELS_PER_SECOND;
        const eventY =
          agentIndex * LANE_HEIGHT + (LANE_HEIGHT - EVENT_HEIGHT) / 2;
        const eventWidth = Math.max(
          60,
          Math.min(200, event.content.length * 8),
        );

        if (
          adjustedX >= eventX &&
          adjustedX <= eventX + eventWidth &&
          adjustedY >= eventY &&
          adjustedY <= eventY + EVENT_HEIGHT
        ) {
          foundEvent = event;
          break;
        }
      }

      setHoveredEvent(foundEvent);
    },
    [
      isPanning,
      panStart,
      panOffset,
      visibleEvents,
      agentPositions,
      PIXELS_PER_SECOND,
    ],
  );

  const handleMouseUp = useCallback(() => {
    setIsPanning(false);
  }, []);

  const handleMouseLeave = useCallback(() => {
    setIsPanning(false);
    setHoveredEvent(null);
  }, []);

  const handleClick = useCallback(
    (e: React.MouseEvent<HTMLCanvasElement>) => {
      if (isPanning) return;

      const rect = canvasRef.current?.getBoundingClientRect();
      if (!rect) return;

      const mouseX = e.clientX - rect.left;
      const adjustedX = mouseX - panOffset.x - AGENT_WIDTH;

      // Click on timeline to change time
      if (e.clientY - rect.top <= TIMELINE_HEIGHT) {
        const newTime = Math.max(
          0,
          Math.min(maxTime, adjustedX / PIXELS_PER_SECOND),
        );
        onTimeChange(newTime);
        return;
      }

      // Click on event
      if (hoveredEvent && onEventClick) {
        onEventClick(hoveredEvent);
      }
    },
    [
      isPanning,
      panOffset,
      hoveredEvent,
      maxTime,
      PIXELS_PER_SECOND,
      onTimeChange,
      onEventClick,
    ],
  );

  // Drawing functions
  const drawBackground = useCallback(
    (ctx: CanvasRenderingContext2D) => {
      ctx.fillStyle = "#1e1e38";
      ctx.fillRect(0, 0, canvasSize.width, canvasSize.height);
    },
    [canvasSize],
  );

  const drawGrid = useCallback(
    (ctx: CanvasRenderingContext2D) => {
      ctx.strokeStyle = "#374151";
      ctx.lineWidth = 1;

      // Vertical grid lines (time markers)
      for (let i = 0; i <= maxTime; i++) {
        const x = AGENT_WIDTH + panOffset.x + i * PIXELS_PER_SECOND;
        if (x > AGENT_WIDTH && x < canvasSize.width) {
          ctx.beginPath();
          ctx.moveTo(x, TIMELINE_HEIGHT);
          ctx.lineTo(x, canvasSize.height);
          ctx.stroke();
        }
      }

      // Horizontal grid lines (agent lanes)
      for (let i = 0; i <= agents.length; i++) {
        const y = TIMELINE_HEIGHT + panOffset.y + i * LANE_HEIGHT;
        if (y > TIMELINE_HEIGHT && y < canvasSize.height) {
          ctx.beginPath();
          ctx.moveTo(AGENT_WIDTH, y);
          ctx.lineTo(canvasSize.width, y);
          ctx.stroke();
        }
      }
    },
    [maxTime, agents.length, panOffset, canvasSize, PIXELS_PER_SECOND],
  );

  const drawTimeline = useCallback(
    (ctx: CanvasRenderingContext2D) => {
      // Timeline background
      ctx.fillStyle = "#1a1a3e";
      ctx.fillRect(0, 0, canvasSize.width, TIMELINE_HEIGHT);

      // Timeline border
      ctx.strokeStyle = "#374151";
      ctx.lineWidth = 1;
      ctx.beginPath();
      ctx.moveTo(0, TIMELINE_HEIGHT);
      ctx.lineTo(canvasSize.width, TIMELINE_HEIGHT);
      ctx.stroke();

      // Time labels
      ctx.fillStyle = "#a1a1aa";
      ctx.font = "11px Inter, sans-serif";
      ctx.textAlign = "center";

      for (let i = 0; i <= maxTime; i++) {
        const x = AGENT_WIDTH + panOffset.x + i * PIXELS_PER_SECOND;
        if (x > AGENT_WIDTH && x < canvasSize.width) {
          ctx.fillText(`${i}s`, x, TIMELINE_HEIGHT - 8);
        }
      }

      // Current time indicator
      const currentTimeX =
        AGENT_WIDTH + panOffset.x + currentTime * PIXELS_PER_SECOND;
      if (currentTimeX > AGENT_WIDTH && currentTimeX < canvasSize.width) {
        ctx.strokeStyle = "#8b5cf6";
        ctx.lineWidth = 2;
        ctx.beginPath();
        ctx.moveTo(currentTimeX, 0);
        ctx.lineTo(currentTimeX, canvasSize.height);
        ctx.stroke();
      }
    },
    [canvasSize, panOffset, maxTime, currentTime, PIXELS_PER_SECOND],
  );

  const drawAgentLabels = useCallback(
    (ctx: CanvasRenderingContext2D) => {
      ctx.fillStyle = "#1a1a3e";
      ctx.fillRect(
        0,
        TIMELINE_HEIGHT,
        AGENT_WIDTH,
        canvasSize.height - TIMELINE_HEIGHT,
      );

      // Agent label border
      ctx.strokeStyle = "#374151";
      ctx.lineWidth = 1;
      ctx.beginPath();
      ctx.moveTo(AGENT_WIDTH, TIMELINE_HEIGHT);
      ctx.lineTo(AGENT_WIDTH, canvasSize.height);
      ctx.stroke();

      agents.forEach((agent, index) => {
        const y = TIMELINE_HEIGHT + panOffset.y + index * LANE_HEIGHT;

        if (y > -LANE_HEIGHT && y < canvasSize.height + LANE_HEIGHT) {
          // Agent color indicator
          ctx.fillStyle = AGENT_COLORS[agent.type];
          ctx.fillRect(8, y + LANE_HEIGHT / 2 - 2, 4, 4);

          // Agent name
          ctx.fillStyle = "#ffffff";
          ctx.font = "14px Inter, sans-serif";
          ctx.textAlign = "left";
          ctx.fillText(agent.type, 20, y + LANE_HEIGHT / 2 + 5);

          // Agent status
          ctx.fillStyle = "#a1a1aa";
          ctx.font = "11px Inter, sans-serif";
          ctx.fillText(agent.status, 20, y + LANE_HEIGHT / 2 + 20);
        }
      });
    },
    [agents, canvasSize, panOffset],
  );

  const drawEvents = useCallback(
    (ctx: CanvasRenderingContext2D) => {
      visibleEvents.forEach((event) => {
        const agentIndex = agentPositions[event.agentId];
        if (agentIndex === undefined) return;

        const eventX =
          AGENT_WIDTH + panOffset.x + event.timestamp * PIXELS_PER_SECOND;
        const eventY =
          TIMELINE_HEIGHT +
          panOffset.y +
          agentIndex * LANE_HEIGHT +
          (LANE_HEIGHT - EVENT_HEIGHT) / 2;
        const eventWidth = Math.max(
          60,
          Math.min(200, event.content.length * 8),
        );

        // Skip if outside visible area
        if (
          eventX + eventWidth < AGENT_WIDTH ||
          eventX > canvasSize.width ||
          eventY + EVENT_HEIGHT < TIMELINE_HEIGHT ||
          eventY > canvasSize.height
        ) {
          return;
        }

        // Event background
        const isHovered = hoveredEvent?.id === event.id;
        ctx.fillStyle = EVENT_COLORS[event.type] || "#6b7280";

        if (isHovered) {
          // Add glow effect for hovered event
          ctx.shadowColor = ctx.fillStyle;
          ctx.shadowBlur = 10;
        }

        // Rounded rectangle
        ctx.beginPath();
        ctx.roundRect(eventX, eventY, eventWidth, EVENT_HEIGHT, 12);
        ctx.fill();

        if (isHovered) {
          ctx.shadowBlur = 0;
        }

        // Event text
        ctx.fillStyle = "#ffffff";
        ctx.font = "12px Inter, sans-serif";
        ctx.textAlign = "left";

        // Truncate text if too long
        let displayText = event.content;
        const maxChars = Math.floor(eventWidth / 8) - 2;
        if (displayText.length > maxChars) {
          displayText = displayText.substring(0, maxChars - 3) + "...";
        }

        ctx.fillText(displayText, eventX + 8, eventY + EVENT_HEIGHT / 2 + 4);

        // Event type indicator
        ctx.fillStyle = "rgba(255, 255, 255, 0.3)";
        ctx.font = "10px Inter, sans-serif";
        ctx.textAlign = "right";
        ctx.fillText(event.type, eventX + eventWidth - 8, eventY + 12);
      });
    },
    [
      visibleEvents,
      agentPositions,
      panOffset,
      canvasSize,
      hoveredEvent,
      PIXELS_PER_SECOND,
    ],
  );

  // Main render function
  const render = useCallback(() => {
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext("2d");
    if (!ctx || !canvas) return;

    // Clear canvas
    ctx.clearRect(0, 0, canvasSize.width, canvasSize.height);

    // Draw layers
    drawBackground(ctx);
    drawGrid(ctx);
    drawEvents(ctx);
    drawTimeline(ctx);
    drawAgentLabels(ctx);
  }, [
    canvasSize,
    drawBackground,
    drawGrid,
    drawEvents,
    drawTimeline,
    drawAgentLabels,
  ]);

  // Effects
  useEffect(() => {
    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [handleResize]);

  useEffect(() => {
    render();
  }, [render]);

  // Animation loop
  useEffect(() => {
    if (isPlaying) {
      const animate = () => {
        render();
        animationRef.current = requestAnimationFrame(animate);
      };
      animationRef.current = requestAnimationFrame(animate);
    } else if (animationRef.current) {
      cancelAnimationFrame(animationRef.current);
    }

    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, [isPlaying, render]);

  return (
    <div
      ref={containerRef}
      className={`waterfall-container relative ${className}`}
      style={{
        height: Math.max(agents.length * LANE_HEIGHT + TIMELINE_HEIGHT, 400),
      }}
    >
      <canvas
        ref={canvasRef}
        className="waterfall-canvas"
        role="presentation"
        aria-label="AI Agent Waterfall Visualization"
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onMouseLeave={handleMouseLeave}
        onClick={handleClick}
      />

      {/* Tooltip */}
      {hoveredEvent && (
        <div
          className="tooltip"
          style={{
            left: mousePosition.x + 10,
            top: mousePosition.y - 10,
            transform:
              mousePosition.x > canvasSize.width / 2
                ? "translateX(-100%)"
                : "none",
          }}
        >
          <div className="font-semibold text-white">{hoveredEvent.type}</div>
          <div className="text-sm text-gray-300 mt-1">
            {hoveredEvent.content}
          </div>
          <div className="text-xs text-gray-400 mt-2">
            Time: {hoveredEvent.timestamp.toFixed(2)}s
            {hoveredEvent.toolName && (
              <span className="ml-2">Tool: {hoveredEvent.toolName}</span>
            )}
          </div>
        </div>
      )}
    </div>
  );
};
